import { useEffect, useState } from "react";
import useMediaQueryHook from "@hooks/useMediaQueryHook";
import { useLocation, useNavigate } from "react-router-dom";
import ExampleImage from "@assets/images/detailExample.png";
import ExerciseDetailsHeader from "./ExerciseDetailsHeader";
import ImageWithFallback from "@components/ui/ImageFallback";
import { ReactComponent as Target } from "@assets/images/purpleTarget.svg";
import { ReactComponent as PinkDumble } from "@assets/images/pinkDumble.svg";
import { ReactComponent as Paramid } from "@assets/images/paramid.svg";
import { ReactComponent as Location } from "@assets/images/yellowLoc.svg";
import { ReactComponent as Time } from "@assets/images/borderTime.svg";
import { ReactComponent as Fire } from "@assets/images/orangeFire.svg";
import { ReactComponent as ExerciseGaol } from "@assets/images/exerciseGaol.svg";
import { ChevronLeft } from "lucide-react";
import WorkoutBreakdownTable from "./WorkoutTable";
import { useTranslation } from "react-i18next";
import { generateExercisePlanPDF } from "@utils/ExercisePdfGen";
import { toast } from "react-toastify";
import ExcercisePlanLoader from "@components/ui/Loaders/ExcercisePlanLoader"; // Add loader
import { downloadExercisePlan, fetchExercise } from "api/exercisePlan";
import ActualDietPlanLoader from "@components/ui/Loaders/ActualDietPlanLoader";
import ExerciseDescCardView from "@features/exercise/ExerciseDescCardView";
import ButtonLoader from "@components/ui/Loaders/ButtonLoader";

const BASE_URL = process.env.REACT_APP_BACKEND_BASE_URL;

const ViewPlan = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { day = null } = location.state || {};

  // State management
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [videoUrl, setVideoUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch exercise data on component mount
  useEffect(() => {
    const loadExerciseData = async () => {
      // Check if day parameter exists
      if (!day) {
        navigate(-1);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        // Fetch data from API
        const response = await fetchExercise(day);
        setData(response?.plan?.data);
        setVideoUrl(response?.plan?.video_url);
      } catch (error) {
        console.error("Error fetching exercise data:", error);
        setError(error.message || "Failed to load exercise data");
        toast.error(t("Failed to load exercise data. Please try again"));
      } finally {
        setIsLoading(false);
      }
    };

    loadExerciseData();
  }, [day, navigate, t]);

  // Nutrient data configuration
  const nutrientData = data
    ? [
        {
          label: t("exercisePlan.duration"),
          key: "duration",
          icon: <Time className="min-w-[16px] min-h-[16px]" />,
          tabColor: "#F1FFF0",
          value: data?.duration,
        },
        {
          label: t("exercisePlan.caloriesBurnt"),
          key: "calories",
          icon: <Fire className="min-w-[16px] min-h-[16px]" />,
          tabColor: "#FFF6F0",
          value: data?.calories,
        },
        {
          label: t("exercisePlan.Goal"),
          key: "goals",
          icon: <ExerciseGaol className="min-w-[16px] min-h-[16px]" />,
          tabColor: "#FFFFF0",
          value: data?.goals,
        },
        {
          label: t("exercisePlan.intensity"),
          key: "intensity",
          icon: <Paramid className="min-w-[16px] min-h-[16px]" />,
          tabColor: "#F0FFFF",
          value: data?.intensity,
        },
        {
          label: t("exercisePlan.workouts"),
          key: "workouts",
          icon: <PinkDumble className="min-w-[16px] min-h-[16px]" />,
          tabColor: "#FFF0FB",
          value:
            typeof data?.workouts === "number" ||
            typeof data?.workouts === "string"
              ? data.workouts
              : "",
        },
        {
          label: t("exercisePlan.focus"),
          key: "focus",
          icon: <Target className="min-w-[16px] min-h-[16px]" />,
          tabColor: "#FFF0FF",
          value: data?.focus,
        },
      ]
    : [];

  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    const match = url.match(/(?:v=|\/)([0-9A-Za-z_-]{11})/);
    return match ? `https://www.youtube.com/embed/${match[1]}` : url;
  };

  const isYouTubeUrl = (url) => {
    const pattern = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\//;
    return pattern.test(url);
  };

  const handleDownloadPdf = async () => {
    // try {
    //   generateExercisePlanPDF(data, t);
    //   toast.info(t("toast.Exercise PDF downloaded successfully"));
    // } catch (error) {
    //   console.error("Error generating PDF:", error);
    //   toast.error(t("toast.Failed to download the file. Please try again"));
    // }
    try {
      setLoading(true);
      const payload = {
        day: day,
      };
      const response = await downloadExercisePlan(payload);

      const fileBlob = new Blob([response.data], { type: "application/pdf" });
      const downloadUrl = window.URL.createObjectURL(fileBlob);

      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = `${data?.title}.pdf`;
      document.body.appendChild(a);
      a.click();

      document.body.removeChild(a);
      window.URL.revokeObjectURL(downloadUrl);
      toast.info(t("toast.Exercise PDF downloaded successfully"));
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error(t("toast.Failed to download the file. Please try again"));
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (isLoading) {
    return <ActualDietPlanLoader exercisePlan={true} />;
  }

  return (
    <>
      <div
        className="mt-[30px] lg:mt-[60px] avoid-page-break p-6 rounded-[18px]"
        style={{
          background: "linear-gradient(150deg, #0771EF 12.89%, #15AAFC 85.22%)",
        }}
      >
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-8">
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate(-1)}
              className="go-back-button p-[6px] lg:p-[10px] bg-white rounded-[100px] text-nowrap"
            >
              <ChevronLeft
                className="w-4 h-4 md:w-6 md:h-6 text-[#0575e6] rounded-full p-[2px]"
                strokeWidth={4}
              />
            </button>
            <h2 className="text-[16px] lg:text-[20px] text-white font-[500] font-[Lato]">
              {data?.title}
            </h2>
          </div>
          <button
            onClick={handleDownloadPdf}
            disabled={loading}
            className="py-[10px] px-3 md:px-[20px] bg-white text-[#0575e6] rounded-[100px] text-[12px]"
          >
            {loading ? <ButtonLoader /> : t("exercisePlan.downloadPlan")}
          </button>
        </div>

        <div className="bg-white rounded-[10px] p-5 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] p-5 rounded-[16px]">
              <h1 className="text-[18px] text-white font-[500] font-[Lato] mb-3">
                {t("exercisePlan.workoutSummary")}
              </h1>

              {/* Desktop layout - 3 columns then 2 columns */}
              <div className="grid grid-cols-2 xl:grid-cols-3 gap-3">
                {nutrientData
                  // ?.slice(0, 3)
                  .map(({ label, icon, value, tabColor }, index) => (
                    <div
                      key={index}
                      className="py-5 px-2 rounded-[17px] bg-white border border-[#E6E6E6] rounded-[16px] flex items-center flex-col xl:flex-row lg:justify-center gap-2 overflow-hidden"
                      style={{
                        boxShadow: "7px 7px 9.5px 0px #0000000D",
                      }}
                    >
                      <div className="flex justify-center">
                        <div
                          className="rounded-full min-w-11 min-h-11 max-w-11 max-h-11 flex items-center justify-center"
                          style={{
                            backgroundColor: tabColor,
                          }}
                        >
                          {icon}
                        </div>
                      </div>
                      <div>
                        <p className="text-[10px] md:text-[12px] font-[Lato] font-[500] text-[#6C6A6ACC] text-center xl:text-start">
                          {label}
                        </p>
                        <div>
                          <p
                            className="text-[10px] md:text-[14px] font-[500] font-[Lato] text-[#6C6A6A] text-center xl:text-start"
                            style={{
                              wordBreak: "break-word",
                              hyphens: "auto",
                              overflowWrap: "break-word",
                            }}
                          >
                            {value}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>

              {/* <div className="hidden xl:grid grid-cols-2 xl:grid-cols-2 gap-3 mt-3">
                {nutrientData
                  ?.slice(3, 5)
                  .map(({ label, icon, value, tabColor }, index) => (
                    <div
                      key={index}
                      className="py-5 px-2 rounded-[17px] bg-white border border-[#E6E6E6] rounded-[16px] flex items-center flex-col xl:flex-row justify-center gap-2 overflow-hidden"
                      style={{
                        boxShadow: "7px 7px 9.5px 0px #0000000D",
                      }}
                    >
                      <div className="flex justify-center">
                        <div
                          className="rounded-full min-w-11 min-h-11 max-w-11 max-h-11 flex items-center justify-center"
                          style={{
                            backgroundColor: tabColor,
                          }}
                        >
                          {icon}
                        </div>
                      </div>
                      <div>
                        <p className="text-[10px] md:text-[10px] font-[500] text-[#6C6A6ACC] text-center md:text-start lg:text-center xl:text-start">
                          {label}
                        </p>
                        <div>
                          <p
                            className="text-[10px] md:text-[14px] font-[500] font-[Lato] text-[#6C6A6A] text-center md:text-start lg:text-center xl:text-start"
                            style={{
                              wordBreak: "break-word",
                              hyphens: "auto",
                              overflowWrap: "break-word",
                            }}
                          >
                            {value}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
              </div> */}

              {/* Mobile layout - all items in 2 columns */}
              {/* <div className="grid xl:hidden grid-cols-2 xl:grid-cols-3 gap-3">
                {nutrientData?.map(
                  ({ label, icon, value, tabColor }, index) => (
                    <div
                      key={index}
                      className="py-5 px-2 rounded-[17px] bg-white border border-[#E6E6E6] rounded-[16px] flex items-center flex-col xl:flex-row justify-center gap-2 overflow-hidden"
                      style={{
                        boxShadow: "7px 7px 9.5px 0px #0000000D",
                      }}
                    >
                      <div className="flex justify-center">
                        <div
                          className="rounded-full min-w-11 min-h-11 max-w-11 max-h-11 flex items-center justify-center"
                          style={{
                            backgroundColor: tabColor,
                          }}
                        >
                          {icon}
                        </div>
                      </div>
                      <div>
                        <p className="text-[10px] md:text-[10px] font-[500] text-[#6C6A6ACC] text-center md:text-start lg:text-center xl:text-start">
                          {label}
                        </p>
                        <div>
                          <p
                            className="text-[10px] md:text-[14px] font-[500] font-[Lato] text-[#6C6A6A] text-center md:text-start lg:text-center xl:text-start"
                            style={{
                              wordBreak: "break-word",
                              hyphens: "auto",
                              overflowWrap: "break-word",
                            }}
                          >
                            {value}
                          </p>
                        </div>
                      </div>
                    </div>
                  )
                )}
              </div> */}
            </div>
            <div className="min-h-[200px] md:min-h-[300px] h-full">
              <video
                className="w-full h-full rounded-md shadow-lg object-cover"
                src={videoUrl}
                poster={data?.image_url}
                playsInline
                controls
              ></video>
            </div>
          </div>
          <div
            className="mt-[30px] lg:mt-[60px] avoid-page-break  p-6 rounded-[18px]"
            style={{
              background:
                "linear-gradient(150deg, #0771EF 12.89%, #15AAFC 85.22%)",
            }}
          >
            <div>
              <h1 className="text-[16px] lg:text-[20px] text-white font-[500] font-[Lato]">
                {t("exercisePlan.workoutDescription")}
              </h1>
              {/* <p className="text-[#6B6B6B] text-[17px] font-[400] font-[Lato]">
              {data?.description}
            </p> */}
              {data?.description && (
                <ExerciseDescCardView exercise={data?.description} />
              )}
            </div>
          </div>

          <WorkoutBreakdownTable breakdown={data?.instances} />
        </div>
      </div>
    </>
  );
};

export default ViewPlan;
